// src/components/PaymentMethodTable.jsx
import React, { useMemo } from "react";
import GenericTable from "./GenericTable";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { IconButton, Tooltip, Box, Chip, Switch, Stack, Typography } from "@mui/material";
import { useCompanies } from "../hooks/useCompanies";
import { getPaymentMethodTipoLabel } from "../constants/paymentMethodTipo";

/**
 * Tabela de Formas de Pagamento
 * Mostra:
 *  - Nome + chips (Default, Emite BPe)
 *  - Status (Switch inline)
 *  - Alcance (Todas as empresas | Nome da Empresa (ID X))
 *  - Ações (Editar/Excluir)
 */
const PaymentMethodTable = ({
  paymentMethods = [],
  rowCount = 0,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onEditMethod,
  onDeleteMethod,
  onDeleteSelected,
  onToggleStatus,
  isLoading,
}) => {
  // Carrega empresas para mapear ids_projetos -> nome
  const { data: compData } = useCompanies(1, 500, "");
  const companies = compData?.companies || [];

  const projectMap = useMemo(() => {
    const map = new Map();
    for (const c of companies) {
      const name = c?.nome || c?.fantasia || c?.razao_social || `Empresa #${c?.id}`;
      map.set(Number(c.id), `${name} (ID ${c.id})`);
    }
    return map;
  }, [companies]);

  const parseProjectIds = (m) => {
    if (Array.isArray(m?.ids_projetos) && m.ids_projetos.length) {
      return m.ids_projetos.map((v) => Number(v)).filter((n) => Number.isFinite(n) && n > 0);
    }
    const legacy = Number(m?.id_projeto);
    if (Number.isFinite(legacy) && legacy > 0) return [legacy];
    return [];
  };

  const scopeLabel = (m) => {
    const ids = parseProjectIds(m);
    if (!ids.length) return "Todas as empresas";
    const labels = ids.map((id) => projectMap.get(id) || `Empresa ID ${id}`);
    if (labels.length <= 2) return labels.join(", ");
    return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
  };

  const rows = useMemo(
    () =>
      paymentMethods.map((m) => ({
        ...m,
        id: m.id,
        nome: m.nome || "N/A",
        tipo_label: getPaymentMethodTipoLabel(m.tipo),
        status_label: Number(m.status) === 1 ? "Ativo" : "Inativo",
        default_label: Number(m.default) === 1 ? "Default" : "",
        alcance_label: scopeLabel(m),
      })),
    [paymentMethods, projectMap]
  );

  const columns = [
    {
      field: "nome",
      headerName: "Forma",
      width: 380,
      sortable: false,
      renderCell: (p) => (
        <Stack spacing={0.3} sx={{ minWidth: 0 }}>
          <Typography fontWeight={600} noWrap title={p.row.nome}>
            {p.row.nome}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" noWrap title={p.row.tipo_label}>
              {p.row.tipo_label}
            </Typography>
            {Number(p.row.default) === 1 && (
              <Chip size="small" label="Default" color="primary" variant="outlined" />
            )}
            {Number(p.row.emitirbpe) === 1 && (
              <Chip size="small" label="Emite BPe" variant="outlined" />
            )}
          </Stack>
        </Stack>
      ),
    },
    {
      field: "alcance_label",
      headerName: "Alcance",
      width: 260,
      sortable: false,
      valueGetter: (p) => p?.row?.alcance_label ?? "—",
      renderCell: (p) => (
        <Typography variant="body2" noWrap title={p.row.alcance_label}>
          {p.row.alcance_label}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Switch
            size="small"
            checked={Number(params.row.status) === 1}
            onChange={() => onToggleStatus?.(params.row)}
          />
          <Chip
            size="small"
            label={params.row.status_label}
            color={Number(params.row.status) === 1 ? "success" : "default"}
            variant="outlined"
          />
        </Stack>
      ),
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => onEditMethod?.(params.row)}>
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" onClick={() => onDeleteMethod?.(params.row.id)}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <GenericTable
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSearch={onSearch}
      isLoading={isLoading}
      onExportPDFTitle="Formas de Pagamento Selecionadas"
      exportFileName="metodos_pagamento"
      onDeleteSelected={onDeleteSelected}
      searchPlaceholder="Buscar por nome…"
      pageSizeOptions={[10, 25, 50, 100]}
      dense
    />
  );
};

export default PaymentMethodTable;
